import {
    ArrayNotEmpty,
    IsArray,
    IsBoolean,
    IsEmail,
    IsEnum,
    IsNotEmpty,
    IsOptional,
    IsPhoneNumber,
    IsString,
    Matches,
    MaxLength,
    MinLength,
    ValidateIf,
    ValidateNested,
} from 'class-validator';
import { Type } from 'class-transformer';
import i18n from 'src/i18n';
import {
    OrganizationRolePermission,
    OrganizationType,
} from 'src/domain/organization';

const __schemaImports = {
    ArrayNotEmpty,
    IsArray,
    IsEmail,
    IsEnum,
    IsNotEmpty,
    IsOptional,
    IsPhoneNumber,
    IsString,
    Matches,
    MaxLength,
    MinLength,
    ValidateIf,
    ValidateNested,
    Type,
    i18n,
    OrganizationRolePermission,
    IsBoolean,
};
void __schemaImports;

export type OrganizationGroupFormValue = {
    id: string;
    name: string;
    parentId?: string;
    levelId: string;
};

export type OrganizationRoleFormValue = {
    id: string;
    name: string;
    groupIds: string[];
    permissions: OrganizationRolePermission[];
};

export type OrganizationHierarchyFormValue = {
    id: string;
    name: string;
    parentId?: string;
};

export type OrganizationFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    licence: number | string;
    phoneNumber?: string;
    organizationName?: string;
    organizationDisplayName?: string;
    disableLogoDesignAdjustments?: boolean;
    initialPassword?: string;
    type: OrganizationType;
    hierarchy: OrganizationHierarchyFormValue[];
    groups: OrganizationGroupFormValue[];
    roles: OrganizationRoleFormValue[];
};

export class OrganizationHierarchyField
    implements OrganizationHierarchyFormValue
{
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    id: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    name: string;

    @IsOptional()
    @IsString()
    parentId?: string;
}

export class OrganizationGroupField implements OrganizationGroupFormValue {
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    id: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    name: string;

    @IsOptional()
    @IsString()
    parentId?: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    levelId: string;
}

export class OrganizationRoleField implements OrganizationRoleFormValue {
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    id: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    name: string;

    @IsArray()
    @ArrayNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @IsString({ each: true })
    groupIds: string[];

    @IsArray()
    @ArrayNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @IsEnum(OrganizationRolePermission, { each: true })
    permissions: OrganizationRolePermission[];
}

export class CreateOrganizationSchema implements OrganizationFormValues {
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName: string;

    @IsOptional()
    @ValidateIf(o => o.phoneNumber?.length !== 0)
    @IsPhoneNumber('NO', {
        message: () => i18n.t('validation.invalidPhone'),
    })
    phoneNumber?: string;

    @IsEmail({}, { message: () => i18n.t('validation.invalidEmail') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    email: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @MinLength(6, { message: () => i18n.t('validation.passwordTooShort') })
    initialPassword: string;

    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    licence: number;

    @IsString()
    @MinLength(3, {
        message: () => i18n.t('validation.lengthBetween', { min: 3, max: 23 }),
    })
    @MaxLength(23, {
        message: () => i18n.t('validation.maxLength', { max: 23 }),
    })
    @Matches(/^((?!\-\-).)*$/, {
        message: () => i18n.t('validation.orgSlugNoDoubleHyphen'),
    })
    @Matches(/^[a-z0-9]+$/, {
        message: () => i18n.t('validation.orgSlugAllowedChars'),
    })
    @Matches(/^[a-z0-9]{1}[a-z0-9\-]+[a-z0-9]{1}$/, {
        message: () => i18n.t('validation.orgSlugStartEnd'),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    organizationName: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    organizationDisplayName: string;

    @IsOptional()
    @IsBoolean()
    disableLogoDesignAdjustments?: boolean;

    @IsEnum(OrganizationType, {
        message: () => i18n.t('validation.requiredField'),
    })
    type: OrganizationType;

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => OrganizationHierarchyField)
    hierarchy: OrganizationHierarchyField[] = [];

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => OrganizationGroupField)
    groups: OrganizationGroupField[] = [];

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => OrganizationRoleField)
    roles: OrganizationRoleField[] = [];
}

export class EditOrganizationSchema implements OrganizationFormValues {
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName: string;

    @IsEmail({}, { message: () => i18n.t('validation.invalidEmail') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    email: string;

    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    licence: number;

    @IsOptional()
    phoneNumber?: string;

    @IsString()
    @MinLength(3, {
        message: () => i18n.t('validation.lengthBetween', { min: 3, max: 23 }),
    })
    @MaxLength(23, {
        message: () => i18n.t('validation.maxLength', { max: 23 }),
    })
    @Matches(/^((?!\-\-).)*$/, {
        message: () => i18n.t('validation.orgSlugNoDoubleHyphen'),
    })
    @Matches(/^[a-z0-9]+$/, {
        message: () => i18n.t('validation.orgSlugAllowedChars'),
    })
    @Matches(/^[a-z0-9]{1}[a-z0-9\-]+[a-z0-9]{1}$/, {
        message: () => i18n.t('validation.orgSlugStartEnd'),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    organizationName: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    organizationDisplayName: string;

    @IsOptional()
    @IsBoolean()
    disableLogoDesignAdjustments?: boolean;

    @IsEnum(OrganizationType, {
        message: () => i18n.t('validation.requiredField'),
    })
    type: OrganizationType;

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => OrganizationHierarchyField)
    hierarchy: OrganizationHierarchyField[] = [];

    @IsOptional()
    initialPassword?: string;

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => OrganizationGroupField)
    groups: OrganizationGroupField[] = [];

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => OrganizationRoleField)
    roles: OrganizationRoleField[] = [];
}

/**
 * Personal administrator fields belong to user records in the separated
 * organization structure, so they must not make organization settings invalid.
 */
export class EditSeparatedOrganizationSchema extends EditOrganizationSchema {
    @IsOptional()
    firstName: string;

    @IsOptional()
    lastName: string;

    @IsOptional()
    email: string;
}
